import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black";
const primaryBtn = "bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50 min-h-[44px]";
const secondaryBtn = "border border-neutral-300 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-50 disabled:opacity-50 min-h-[44px]";

const SOURCE_LABEL = {
  database: "Saved here (database)",
  environment: "Server environment variables (AWS_*)",
  none: "Not configured",
};

/**
 * AWS S3 settings used for every image upload (tenant logos, banners, category,
 * service and seller images). The secret access key is write-only: the server
 * never sends it back, so this page can only replace it.
 */
export default function SuperStorage() {
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ region: "ap-south-1", bucket: "", access_key_id: "", secret_access_key: "", cdn_base: "" });
  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const apply = (v) => {
    setView(v);
    // The access key ID comes back masked; both keys are typed only to change them.
    setForm({ region: v.region || "ap-south-1", bucket: v.bucket || "", access_key_id: "", secret_access_key: "", cdn_base: v.cdn_base || "" });
  };
  useEffect(() => { api.get("/super/config/storage").then((r) => apply(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy("save"); setTestResult(null);
    try {
      const r = await api.put("/super/config/storage", form);
      apply(r.data); toast.success("Storage settings saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Check the region, bucket name and access key ID");
    } finally { setBusy(null); }
  };

  const test = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const r = await api.post("/super/config/storage/test");
      setTestResult(r.data);
    } catch { setTestResult({ ok: false, message: "The test could not be run" }); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the saved storage settings? Uploads will use the server's AWS_* environment variables, if set.")) return;
    setBusy("remove"); setTestResult(null);
    try {
      const r = await api.delete("/super/config/storage");
      apply(r.data); toast.success("Saved settings removed");
    } finally { setBusy(null); }
  };

  if (!view) return <div className="text-sm text-neutral-500">Loading…</div>;
  const savedHere = view.source === "database";

  return (
    <div data-testid="super-storage">
      <div className="admin-overline">Platform</div>
      <h1 className="admin-heading text-3xl font-bold mt-1">Image storage (AWS S3)</h1>
      <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
        Used for tenant logos and banners, and category, service and seller images. Each tenant's files are stored under its own folder in the bucket.
      </p>

      <div className="bg-white border border-neutral-200 p-4 sm:p-6 mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="super-storage-status">
        <div><div className="admin-overline">Status</div><div className="font-semibold mt-1" data-testid="super-storage-configured">{view.configured ? "Ready" : "Not configured"}</div></div>
        <div className="min-w-0"><div className="admin-overline">In use from</div><div className="font-semibold mt-1 break-words">{SOURCE_LABEL[view.source]}</div></div>
        <div className="min-w-0"><div className="admin-overline">Access key ID</div><div className="font-mono text-sm mt-1 break-anywhere" data-testid="super-storage-key-masked">{view.access_key_id_masked || "—"}</div></div>
        <div><div className="admin-overline">Secret access key</div><div className="font-semibold mt-1">{view.has_secret ? "Stored (hidden)" : "Not set"}</div></div>
        {view.unreadable_secret && (
          <div className="sm:col-span-2 lg:col-span-4 text-sm text-red-700">
            The saved secret can no longer be read because the server's encryption key changed. Enter the secret access key again and save.
          </div>
        )}
      </div>

      <form onSubmit={save} className="bg-white border border-neutral-200 p-4 sm:p-6 mt-6 grid md:grid-cols-2 gap-4" data-testid="super-storage-form" autoComplete="off">
        <div>
          <label className="admin-overline" htmlFor="storage-region">AWS region</label>
          <input id="storage-region" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value.trim() })}
            className={`${inputClass} font-mono`} placeholder="ap-south-1" data-testid="super-storage-region" />
        </div>
        <div>
          <label className="admin-overline" htmlFor="storage-bucket">S3 bucket name</label>
          <input id="storage-bucket" required value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value.trim() })}
            className={`${inputClass} font-mono`} data-testid="super-storage-bucket" />
        </div>
        <div>
          <label className="admin-overline" htmlFor="storage-key">AWS access key ID</label>
          <input id="storage-key" required={!savedHere} autoComplete="off" value={form.access_key_id} onChange={(e) => setForm({ ...form, access_key_id: e.target.value.trim() })}
            className={`${inputClass} font-mono`} placeholder={savedHere ? `${view.access_key_id_masked} — leave blank to keep it` : "AKIA…"} data-testid="super-storage-key" />
        </div>
        <div>
          <label className="admin-overline" htmlFor="storage-secret">AWS secret access key</label>
          <input id="storage-secret" type="password" autoComplete="new-password" required={!(savedHere && view.has_secret)}
            value={form.secret_access_key} onChange={(e) => setForm({ ...form, secret_access_key: e.target.value })}
            className={`${inputClass} font-mono`} placeholder={savedHere && view.has_secret ? "Stored — leave blank to keep it" : ""} data-testid="super-storage-secret" />
          <div className="text-[11px] text-neutral-500 mt-1">Encrypted on the server. It is never shown again or sent to any browser or app.</div>
        </div>
        <div className="md:col-span-2">
          <label className="admin-overline" htmlFor="storage-cdn">CDN base URL (optional)</label>
          <input id="storage-cdn" type="url" value={form.cdn_base} onChange={(e) => setForm({ ...form, cdn_base: e.target.value.trim() })}
            className={`${inputClass} font-mono`} placeholder="https://media.example.com" data-testid="super-storage-cdn" />
          <div className="text-[11px] text-neutral-500 mt-1">The CloudFront (or other) address that serves the bucket. Leave blank to use the bucket's own S3 address.</div>
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-2">
          <button type="submit" disabled={busy === "save"} className={primaryBtn} data-testid="super-storage-save">{busy === "save" ? "Saving..." : "Save settings"}</button>
          <button type="button" onClick={test} disabled={busy === "test" || !view.configured} className={secondaryBtn} data-testid="super-storage-test">{busy === "test" ? "Testing..." : "Send a test upload"}</button>
          {savedHere && <button type="button" onClick={remove} disabled={busy === "remove"} className={secondaryBtn} data-testid="super-storage-remove">Remove saved settings</button>}
        </div>
        {testResult && (
          <div className={`md:col-span-2 text-sm break-anywhere ${testResult.ok ? "text-green-700" : "text-red-700"}`} data-testid="super-storage-test-result">
            {testResult.message}{testResult.url ? ` — ${testResult.url}` : ""}
          </div>
        )}
      </form>
    </div>
  );
}
